import { createHashLike } from './hash.js';

/**
 * Markdown protection for machine translation (Section 11.1): code, URLs, attachment references
 * and identifiers are replaced by validated placeholders before translation and restored after.
 * A result whose placeholders do not round-trip exactly is FAILED, never READY.
 */
export interface ProtectedText {
  text: string;
  placeholders: string[];
}

const PATTERNS: RegExp[] = [
  /```[\s\S]*?```/g, // fenced code blocks
  /`[^`\n]+`/g, // inline code
  /!?\[[^\]\n]*\]\([^)\s]+\)/g, // markdown links/images (link text is kept as a unit)
  /\bhttps?:\/\/[^\s)>\]]+/g, // bare URLs
  /\b(?:VAM|REQ)-\d+\b/g, // tracker keys
  /\b[A-Z][A-Z0-9_]+-\d+\b/g, // Jira-style keys
  /@[A-Za-z0-9_.-]+/g, // mentions/handles
  /\b[0-9a-f]{7,40}\b/g, // commit SHAs
  /\battachment:[0-9a-f-]{36}\b/g,
];

const token = (index: number) => `⟦${index}⟧`;

export function protectMarkdown(input: string): ProtectedText {
  const placeholders: string[] = [];
  let text = input;
  for (const pattern of PATTERNS) {
    text = text.replace(pattern, (match) => {
      // Do not re-protect text already turned into a placeholder.
      if (/^⟦\d+⟧$/.test(match)) return match;
      placeholders.push(match);
      return token(placeholders.length - 1);
    });
  }
  return { text, placeholders };
}

export function restoreMarkdown(translated: string, placeholders: readonly string[]): { ok: true; text: string } | { ok: false; reason: string } {
  const found = [...translated.matchAll(/⟦(\d+)⟧/g)].map((m) => Number(m[1]));
  const expected = placeholders.map((_, i) => i);
  const sortedFound = [...found].sort((a, b) => a - b);
  if (sortedFound.length !== expected.length || sortedFound.some((v, i) => v !== expected[i])) {
    return { ok: false, reason: 'Protected placeholders did not round-trip' };
  }
  let restored = translated;
  // Restore in reverse so nested placeholders (e.g. a URL inside protected link text) resolve.
  for (let i = placeholders.length - 1; i >= 0; i--) restored = restored.split(token(i)).join(placeholders[i]!);
  if (/⟦\d+⟧/.test(restored)) return { ok: false, reason: 'Unresolved placeholder remains' };
  return { ok: true, text: restored };
}

/** Canonical line endings before hashing (Section 11.1). */
export const canonicalizeText = (text: string) => text.replace(/\r\n?/g, '\n');

/** Identity of a persisted translation: field revision, locales and translator configuration. */
export function translationKey(input: {
  entityKind: string;
  entityId: string;
  field: string;
  contentRevision: number;
  sourceLocale: string;
  targetLocale: string;
  translatorConfigVersion: string;
  sourceText: string;
}): { key: string; sourceHash: string } {
  const sourceHash = createHashLike(`${input.field}\u0000${input.contentRevision}\u0000${input.sourceLocale}\u0000${canonicalizeText(input.sourceText)}`);
  const key = [input.entityKind, input.entityId, input.field, input.contentRevision, input.sourceLocale, input.targetLocale, input.translatorConfigVersion, sourceHash].join(':');
  return { key, sourceHash };
}

/** Maps a BCP 47 tag to a supported UI locale (Section 11.1): pt-* → pt-BR, es-* → es, otherwise en. */
export function toSupportedLocale(tag: string | null | undefined): 'en' | 'pt-BR' | 'es' {
  const lower = (tag ?? '').toLowerCase();
  if (lower.startsWith('pt')) return 'pt-BR';
  if (lower.startsWith('es')) return 'es';
  return 'en';
}

/** Whether a reader in `target` needs a translation of content authored in `source`. */
export function needsTranslation(source: string, target: string): boolean {
  if (!source || source === 'und') return true;
  return toSupportedLocale(source) !== toSupportedLocale(target) || (!['en', 'pt', 'es'].some((p) => source.toLowerCase().startsWith(p)));
}

/** Splits long text at paragraph boundaries into chunks of at most `maxChars`. */
export function chunkParagraphs(text: string, maxChars = 6000): string[] {
  const paragraphs = text.split(/\n{2,}/);
  const chunks: string[] = [];
  let current = '';
  for (const paragraph of paragraphs) {
    const candidate = current ? `${current}\n\n${paragraph}` : paragraph;
    if (candidate.length <= maxChars) {
      current = candidate;
      continue;
    }
    if (current) chunks.push(current);
    if (paragraph.length <= maxChars) {
      current = paragraph;
    } else {
      for (let i = 0; i < paragraph.length; i += maxChars) chunks.push(paragraph.slice(i, i + maxChars));
      current = '';
    }
  }
  if (current) chunks.push(current);
  return chunks.length ? chunks : [''];
}
